import React from 'react';
import { StyleSheet, View } from 'react-native';
import type {
  AddressType,
  CloudinaryTransformConfig,
} from '@sweetgreen/garnish';
import { Button } from '@sweetgreen/garnish';
import {
  BodyText,
  capitalizeFirstLetter,
  Card,
  CloudinaryImage,
  theme,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';
import { ignoreTimezone } from '@order/utils';

import {
  useKustomerAvailability,
  useKustomerContactUsHandler,
} from '../../integrations/kustomer';

export const OrderCard = (props: OrderCardProps) => {
  return (
    <Card testID="order-card" onPress={props.onPress}>
      <View style={styles.content}>
        <OrderCardImage {...props} />
        <View style={styles.info}>
          <OrderCardTitle {...props} />
          <OrderCardLocation {...props} />
          <OrderCardDescription {...props} />
          <OrderCardTotal {...props} />
          {props.isCancellable ? <OrderCardQuickCancelButton /> : null}
        </View>
      </View>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardImage = React.memo((props: Pick<OrderCardProps, 'assetUrl'>) => (
  <CloudinaryImage
    baseUrl={props.assetUrl}
    config={ORDER_CARD_IMG_CONFIG}
    style={styles.imageContents}
    accessibilityIgnoresInvertColors
  />
));

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardTitle = ({
  wantedTime,
  orderType,
}: Pick<OrderCardProps, 'wantedTime' | 'orderType'>) => {
  const { t } = useLocalizationContext();

  const normalizedWantedTime = ignoreTimezone(wantedTime);
  const capitalizedOrderType = capitalizeFirstLetter(orderType);
  const title = normalizedWantedTime
    ? t('account.order-card.title', {
        wantedTime: normalizedWantedTime,
        orderType: capitalizedOrderType,
      })
    : capitalizedOrderType;

  return <BodyText bold>{title}</BodyText>;
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardLocation = ({
  orderType,
  restaurantName,
  addressName,
}: Pick<OrderCardProps, 'orderType' | 'restaurantName' | 'addressName'>) => (
  <BodyText>
    {orderType === 'delivery' && addressName
      ? capitalizeFirstLetter(addressName)
      : restaurantName}
  </BodyText>
);
// ────────────────────────────────────────────────────────────────────────────────

const OrderCardDescription = ({
  description,
}: Pick<OrderCardProps, 'description'>) => (
  <View style={styles.description}>
    <BodyText numberOfLines={2} ellipsizeMode="tail">
      {description}
    </BodyText>
  </View>
);

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardTotal = ({ total }: Pick<OrderCardProps, 'total'>) => {
  const { formatPrice } = useLocalizationContext();

  return <BodyText>{formatPrice(total, 'USD')}</BodyText>;
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardQuickCancelButton = () => {
  const { t } = useLocalizationContext();
  const { handleContactUs, handlingContactUs } = useKustomerContactUsHandler(
    useKustomerAvailability(),
    'order-history',
  );

  return (
    <Button
      width="100%"
      palette="secondary"
      style={styles.cancelButton}
      isLoading={handlingContactUs}
      onPress={handleContactUs}
    >
      {t('account.orders.cancel')}
    </Button>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────────

const ORDER_CARD_IMG = {
  width: 118,
  height: 140,
  offsetTop: -6,
  offsetLeft: -5,
};

const ORDER_CARD_IMG_CONFIG: CloudinaryTransformConfig = {
  crop: 'fill',
  width: ORDER_CARD_IMG.width,
  height: ORDER_CARD_IMG.height,
};

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing['6'],
  },
  info: {
    flex: 1,
    marginLeft: theme.spacing['2'],
  },
  imageContents: {
    width: ORDER_CARD_IMG.width,
    height: ORDER_CARD_IMG.height,
    marginTop: ORDER_CARD_IMG.offsetTop,
    marginLeft: ORDER_CARD_IMG.offsetLeft,
  },
  description: {
    marginVertical: theme.spacing['2'],
  },
  cancelButton: {
    marginRight: 'auto',
    marginTop: theme.spacing['2'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────────

type OrderCardProps = {
  readonly orderId: string;
  readonly orderType: AddressType;
  readonly wantedTime: string;
  readonly restaurantName?: string;
  readonly addressName?: string;
  readonly description: string;
  readonly total: number;
  readonly assetUrl: string;
  readonly isCancellable?: boolean;
  readonly onPress: () => void;
};
